import { ConfirmDialogProvider } from "@/components/confirm-dialog-provider";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { NewVersionBanner } from "@/components/new-version-banner";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import { Suspense, lazy, useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { ScrollToTop } from "./components/scroll-to-top";
import { useAuth } from "@/_core/hooks/useAuth";
import { syncAppPath } from "@/lib/app-path-trail";
import { isAdminUser } from "@/lib/auth-utils";
import { applyPublicDocument } from "@/lib/apply-public-document";
import { resolvePublicDocument } from "@shared/public-document";

const CarListings = lazy(() => import("./pages/CarListings"));

const NotFound = lazy(() => import("@/pages/NotFound"));
const CarDetails = lazy(() => import("./pages/CarDetails"));
const AddCar = lazy(() => import("./pages/AddCar"));
const AdminManageCars = lazy(() => import("./pages/dashboard/AdminManageCars"));
const CarArchivePage = lazy(() => import("./pages/dashboard/CarArchivePage"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Register = lazy(() => import("./pages/Register"));
const RegisterDealership = lazy(() => import("./pages/RegisterDealership"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PhoneLogin = lazy(() => import("./pages/PhoneLogin"));
const FAQs = lazy(() => import("./pages/FAQs"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const CarCondition = lazy(() => import("./pages/CarCondition"));
const HowWeTest = lazy(() => import("./pages/HowWeTest"));
const TestPoints = lazy(() => import("./pages/TestPoints"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Evaluation = lazy(() => import("./pages/Evaluation"));
const Help = lazy(() => import("./pages/Help"));
const Support = lazy(() => import("./pages/Support"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DashboardRedirect = lazy(() => import("./pages/dashboard/DashboardRedirect"));
const BidderDashboardHome = lazy(() => import("./pages/dashboard/BidderDashboardHome"));
const BidderFavoritesPage = lazy(() => import("./pages/dashboard/BidderFavoritesPage"));
const BidderBidsPage = lazy(() => import("./pages/dashboard/BidderBidsPage"));
const BidderRecentPage = lazy(() => import("./pages/dashboard/BidderRecentPage"));
const BidderNotificationsPage = lazy(() => import("./pages/dashboard/BidderNotificationsPage"));
const BidderMessagesPage = lazy(() => import("./pages/dashboard/BidderMessagesPage"));
const BidderProfilePage = lazy(() => import("./pages/dashboard/BidderProfilePage"));
const AdminDashboardHome = lazy(() => import("./pages/dashboard/AdminDashboardHome"));
const AdminProfilePage = lazy(() => import("./pages/dashboard/AdminProfilePage"));
const BoostListingsPage = lazy(() => import("./pages/dashboard/BoostListingsPage"));
const AdminBoostsPage = lazy(() => import("./pages/dashboard/AdminBoostsPage"));
const AdminSiteSettingsPage = lazy(() => import("./pages/dashboard/AdminSiteSettingsPage"));
const AdminUsersPage = lazy(() => import("./pages/dashboard/AdminUsersPage"));
const DealershipListingsPage = lazy(() => import("./pages/dashboard/DealershipListingsPage"));
const ListingFeesPage = lazy(() => import("./pages/dashboard/ListingFeesPage"));
const BidderCurrencyPage = lazy(() => import("./pages/dashboard/BidderCurrencyPage"));
const AdminManageAuctions = lazy(() => import("./pages/dashboard/AdminManageAuctions"));
const AdminDealershipApplicationsPage = lazy(
  () => import("./pages/dashboard/AdminDealershipApplicationsPage"),
);
const AdminListingFeesPage = lazy(() => import("./pages/dashboard/AdminListingFeesPage"));
const AdminBrandsPage = lazy(() => import("./pages/dashboard/AdminBrandsPage"));
const AdminBroadcastPage = lazy(() => import("./pages/dashboard/AdminBroadcastPage"));
const AdminCurrencyPage = lazy(() => import("./pages/dashboard/AdminCurrencyPage"));
const AdminActivityPage = lazy(() => import("./pages/dashboard/AdminActivityPage"));
const AdminUserDetailPage = lazy(() => import("./pages/dashboard/AdminUserDetailPage"));
const AdminStatusPage = lazy(() => import("./pages/dashboard/AdminStatusPage"));
const DealershipPublicPage = lazy(() => import("./pages/DealershipPublicPage"));

function StatusRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => setLocation("/dashboard/admin/status"), [setLocation]);
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <p className="text-muted-foreground text-sm">جاري التوجيه...</p>
    </div>
  );
}

function FavoritesRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => setLocation("/dashboard/bidder/favorites"), [setLocation]);
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <p className="text-muted-foreground text-sm">جاري التوجيه...</p>
    </div>
  );
}

function ProfileRedirect() {
  const { user, loading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || !user) return;
    setLocation(isAdminUser(user) ? "/dashboard/admin/profile" : "/dashboard/bidder/profile");
  }, [loading, isAuthenticated, user, setLocation]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-muted-foreground text-sm">جاري التحميل...</p>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-muted-foreground text-sm">جاري التوجيه...</p>
      </div>
    );
  }

  return <UserProfile />;
}

function AppRouter() {
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/listings"} component={CarListings} />
      <Route path={"/car/:id"} component={CarDetails} />
      <Route path={"/dealership/:id"} component={DealershipPublicPage} />
      <Route path={"/add-car"} component={AddCar} />
      <Route path={"/edit-car/:id"} component={AddCar} />
      <Route path={"/dashboard/admin/cars"} component={AdminManageCars} />
      <Route path={"/dashboard/admin/auctions"} component={AdminManageAuctions} />
      <Route path={"/dashboard/admin/dealerships"} component={AdminDealershipApplicationsPage} />
      <Route path={"/dashboard/admin/listing-fees"} component={AdminListingFeesPage} />
      <Route path={"/dashboard/admin/brands"} component={AdminBrandsPage} />
      <Route path={"/dashboard/admin/broadcast"} component={AdminBroadcastPage} />
      <Route path={"/dashboard/admin/currency"} component={AdminCurrencyPage} />
      <Route path={"/dashboard/admin/activity"} component={AdminActivityPage} />
      <Route path={"/dashboard/admin/status"} component={AdminStatusPage} />
      <Route path={"/dashboard/admin/archive"} component={CarArchivePage} />
      <Route path={"/dashboard/bidder/archive"} component={CarArchivePage} />
      <Route path={"/profile"} component={ProfileRedirect} />
      <Route path={"/register/dealership"} component={RegisterDealership} />
      <Route path={"/register"} component={Register} />
      <Route path={"/login"} component={Login} />
      <Route path={"/login/phone"} component={PhoneLogin} />
      <Route path={"/auth/callback"} component={AuthCallback} />
      <Route path={"/forgot-password"} component={ForgotPassword} />
      <Route path={"/reset-password"} component={ResetPassword} />
      <Route path={"/faqs"} component={FAQs} />
      <Route path={"/about"} component={AboutUs} />
      <Route path={"/car-condition"} component={CarCondition} />
      <Route path={"/how-we-test"} component={HowWeTest} />
      <Route path={"/test-points"} component={TestPoints} />
      <Route path={"/contact"} component={ContactUs} />
      <Route path={"/evaluation"} component={Evaluation} />
      <Route path={"/help"} component={Help} />
      <Route path={"/support"} component={Support} />
      <Route path={"/status"} component={StatusRedirect} />
      <Route path={"/privacy"} component={PrivacyPolicy} />
      <Route path={"/terms"} component={TermsOfUse} />
      <Route path={"/cookies"} component={CookiePolicy} />
      <Route path={"/favorites"} component={FavoritesRedirect} />
      <Route path={"/dashboard/bidder/favorites"} component={BidderFavoritesPage} />
      <Route path={"/dashboard/bidder/bids"} component={BidderBidsPage} />
      <Route path={"/dashboard/bidder/recent"} component={BidderRecentPage} />
      <Route path={"/dashboard/bidder/notifications"} component={BidderNotificationsPage} />
      <Route path={"/dashboard/bidder/messages/:threadId?"} component={BidderMessagesPage} />
      <Route path={"/dashboard/bidder/listings"} component={DealershipListingsPage} />
      <Route path={"/dashboard/bidder/listing-fees"} component={ListingFeesPage} />
      <Route path={"/dashboard/bidder/currency"} component={BidderCurrencyPage} />
      <Route path={"/dashboard/bidder/boosts"} component={BoostListingsPage} />
      <Route path={"/dashboard/bidder/profile"} component={BidderProfilePage} />
      <Route path={"/dashboard/bidder"} component={BidderDashboardHome} />
      <Route path={"/dashboard/admin/profile"} component={AdminProfilePage} />
      <Route path={"/dashboard/admin/boosts"} component={AdminBoostsPage} />
      <Route path={"/dashboard/admin/settings"} component={AdminSiteSettingsPage} />
      <Route path={"/dashboard/admin/users/:id"} component={AdminUserDetailPage} />
      <Route path={"/dashboard/admin/users"} component={AdminUsersPage} />
      <Route path={"/dashboard/admin"} component={AdminDashboardHome} />
      <Route path={"/dashboard"} component={DashboardRedirect} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function AppShell() {
  const [loc] = useLocation();
  useEffect(() => {
    syncAppPath(loc);
  }, [loc]);
  useEffect(() => {
    const path = loc.split("?")[0] || "/";
    if (path.startsWith("/car/")) return;
    applyPublicDocument(resolvePublicDocument(path));
  }, [loc]);
  // Hide site header/footer on the dashboard and on focused auth flows
  // (password recovery + OAuth callback) so the recovery session's logged-in
  // state isn't shown in the global header mid-reset.
  const hideChrome =
    loc.startsWith("/dashboard") ||
    loc.startsWith("/login") ||
    loc.startsWith("/register") ||
    loc.startsWith("/reset-password") ||
    loc.startsWith("/auth/callback");

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <ScrollToTop />
      {!hideChrome ? <Header /> : null}
      <main className="w-full flex-1">
        <Suspense
          fallback={
            <div className="flex min-h-[40vh] items-center justify-center">
              <p className="text-muted-foreground text-sm">جاري التحميل...</p>
            </div>
          }
        >
          <AppRouter />
        </Suspense>
      </main>
      {!hideChrome ? <Footer /> : null}
      <NewVersionBanner />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <ConfirmDialogProvider>
            <Toaster />
            <CookieConsentBanner />
            <AppShell />
          </ConfirmDialogProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
